/* ==========================================================================
   GBSC — Theme JS
   Cart drawer + nav drawer + reveal-on-scroll.
   Cart wraps Shopify /cart.js. Tweaks panel stripped for production.

   2026-08-21 perf rework:
   - Optimistic qty/remove UI — the number changes on click, not after the network.
   - Renders from the /cart/change.js response (it returns the full cart);
     the old code did a second /cart.js round-trip after every change.
   - Rapid +/- clicks coalesce into one debounced request per line.
   - Lines are addressed by item.key (stable) instead of line number
     (shifts when a line above is removed).
   ========================================================================== */

(function () {
  'use strict';

  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  // ============ DRAWERS ============
  const backdrop   = $('#backdrop');
  const cartDrawer = $('#cartDrawer');
  const navDrawer  = $('#navDrawer');

  function openDrawer(el) {
    if (!el) return;
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    backdrop && backdrop.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeAllDrawers() {
    [cartDrawer, navDrawer].forEach(el => {
      if (!el) return;
      el.classList.remove('open');
      el.setAttribute('aria-hidden', 'true');
    });
    backdrop && backdrop.classList.remove('open');
    document.body.style.overflow = '';
  }

  // Global triggers (data attributes so any template can wire one up)
  document.addEventListener('click', (e) => {
    const cartTrigger = e.target.closest('[data-open-cart]');
    const navTrigger  = e.target.closest('[data-open-nav]');
    const closeTrigger = e.target.closest('[data-close-drawer]');
    if (cartTrigger) { e.preventDefault(); openDrawer(cartDrawer); renderCart(); }
    if (navTrigger)  { e.preventDefault(); openDrawer(navDrawer); }
    if (closeTrigger) { e.preventDefault(); closeAllDrawers(); }
  });

  backdrop && backdrop.addEventListener('click', closeAllDrawers);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAllDrawers(); });

  // ============ CART (Shopify /cart.js) ============
  async function getCart() {
    const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return r.json();
  }

  async function addToCart(payload, quantity = 1) {
    // Accept either a FormData (preserves properties[...] for bundles, gift cards, etc.)
    // or (variantId, quantity) for programmatic adds.
    let body;
    let headers;
    if (payload instanceof FormData) {
      body = new URLSearchParams(payload).toString();
      headers = { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' };
    } else {
      body = JSON.stringify({ items: [{ id: payload, quantity }] });
      headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    }
    const r = await fetch('/cart/add.js', { method: 'POST', headers, body });
    if (!r.ok) throw new Error('Add to cart failed');
    await renderCart();
    openDrawer(cartDrawer);
  }

  // --- Debounced, coalesced line updates keyed by item.key ---
  // Click → UI updates instantly → one request fires 300ms after the last click.
  // If more clicks land while a request is in flight, the final quantity is
  // re-sent once, after it returns. Render only happens when nothing is pending,
  // so the server response never clobbers a newer optimistic value.
  const pendingLines = new Map(); // key -> { qty, timer, inFlight }

  function queueLineUpdate(key, quantity, delay = 300) {
    let state = pendingLines.get(key);
    if (!state) { state = { qty: quantity, timer: null, inFlight: false }; pendingLines.set(key, state); }
    state.qty = quantity;
    clearTimeout(state.timer);
    state.timer = setTimeout(() => flushLine(key), delay);
  }

  async function flushLine(key) {
    const state = pendingLines.get(key);
    if (!state || state.inFlight) return;
    state.inFlight = true;
    const sentQty = state.qty;
    let cart = null;
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: sentQty })
      });
      if (!r.ok) throw new Error('Cart change failed');
      cart = await r.json(); // /cart/change.js returns the full updated cart
    } catch (err) {
      console.error(err);
      state.inFlight = false;
      pendingLines.delete(key);
      renderCart(); // resync from the server
      return;
    }
    state.inFlight = false;
    if (state.qty !== sentQty) { flushLine(key); return; } // clicks landed mid-flight
    pendingLines.delete(key);
    if (pendingLines.size === 0) renderCartFrom(cart);
  }

  function money(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function thumbSrc(url) {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?|$)/, '_160x160$1$2');
  }

  async function renderCart() {
    if (!cartDrawer) return;
    renderCartFrom(await getCart());
  }

  function renderCartFrom(cart) {
    if (!cartDrawer || !cart || !cart.items) return;
    const count = $('#cartCount');
    if (count) count.textContent = cart.item_count;

    const body = $('[data-cart-body]', cartDrawer);
    const foot = $('[data-cart-foot]', cartDrawer);
    if (!body || !foot) return;

    if (!cart.items.length) {
      body.innerHTML = `<div class="empty-state"><h4>Nothing here yet.</h4><p>That's on purpose. Start with the Kit.</p></div>`;
      foot.innerHTML = `<a href="/collections/the-kit" class="btn btn-primary" data-close-drawer>Shop the Kit</a>`;
      return;
    }

    function renderProps(props) {
      if (!props) return '';
      const entries = Object.entries(props).filter(([k, v]) => k && !k.startsWith('_') && v != null && v !== '');
      if (!entries.length) return '';
      return `<ul class="cart-line__props">${
        entries.map(([k, v]) => `<li><span class="cart-line__prop-key">${k}:</span> ${v}</li>`).join('')
      }</ul>`;
    }

    body.innerHTML = cart.items.map((item) => `
      <div class="cart-line" data-key="${item.key}">
        <div class="thumb">${item.image ? `<img src="${thumbSrc(item.image)}" alt="${item.product_title}" loading="lazy" width="72" height="72" style="width:100%;height:100%;object-fit:cover;">` : ''}</div>
        <div>
          <div class="name">${item.product_title}</div>
          <div class="sku mono">${item.sku || ''}</div>
          ${item.product_title.includes('Tee') && item.options_with_values?.find(o => o.name === 'Size') ? `<div class="product-option"><span class="option-label">Size:</span> <span class="option-value">${item.options_with_values.find(o => o.name === 'Size').value}</span></div>` : ''}
          ${renderProps(item.properties)}
          <div class="qty">
            <button data-qty-delta="-1" aria-label="Decrease">−</button>
            <span data-qty-value>${item.quantity}</span>
            <button data-qty-delta="1" aria-label="Increase">+</button>
          </div>
          <button data-qty-delta="remove" class="cart-line-remove" style="background:none;border:none;padding:0;margin-top:8px;color:var(--hs,#3B5364);text-decoration:underline;cursor:pointer;font-size:12px;font-family:inherit;letter-spacing:0.02em;opacity:0.7;">Remove</button>
        </div>
        <div class="price mono">${money(item.final_line_price)}</div>
      </div>
    `).join('');

    foot.innerHTML = `
      <div class="subtotal"><span class="label">Subtotal</span><span class="amt">${money(cart.total_price)}</span></div>
      <p class="caption">Free shipping over $60. Taxes calculated at checkout.</p>
      <a href="/checkout" class="btn btn-primary">Check out →</a>
      <button class="btn btn-ghost" data-close-drawer>Keep shopping</button>
    `;
  }

  // One delegated listener for +/−/remove — bound once, survives every re-render.
  // Reads the on-screen quantity (not a captured cart object), so rapid clicks
  // never work from stale data.
  cartDrawer && cartDrawer.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-delta]');
    if (!btn) return;
    const lineEl = btn.closest('[data-key]');
    if (!lineEl) return;
    const key = lineEl.dataset.key;
    const delta = btn.dataset.qtyDelta;

    if (delta === 'remove') {
      lineEl.style.opacity = '0.4';
      lineEl.style.pointerEvents = 'none';
      queueLineUpdate(key, 0, 0); // removes fire immediately
      return;
    }

    const qtyEl = lineEl.querySelector('[data-qty-value]');
    if (!qtyEl) return;
    const next = Math.max(0, (parseInt(qtyEl.textContent, 10) || 0) + parseInt(delta, 10));
    qtyEl.textContent = next; // optimistic — instant feedback
    if (next === 0) {
      lineEl.style.opacity = '0.4';
      lineEl.style.pointerEvents = 'none';
    }
    queueLineUpdate(key, next);
  });

  // Add-to-cart forms (Shopify native form markup).
  // Pass the entire FormData so all fields — including properties[...] from
  // bundle / gift-card / app-block widgets — are sent to /cart/add.js.
  // Skip if a section-level handler already called preventDefault (avoids double-add).
  document.addEventListener('submit', async (e) => {
    if (e.defaultPrevented) return;
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    try { await addToCart(new FormData(form)); } catch (err) { console.error(err); }
  });

  // ============ REVEAL ON SCROLL ============
  if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    $$('.reveal, .section, .kit-callout, .dispatch, .hero-photo').forEach(el => {
      el.classList.add('reveal');
      io.observe(el);
    });
  } else {
    $$('.reveal, .section, .kit-callout, .dispatch, .hero-photo').forEach(el => el.classList.add('in'));
  }

  // Init
  if (cartDrawer) renderCart();

  window.GBSC = { addToCart, renderCart, openDrawer, closeAllDrawers };
})();